import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Skeleton,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, Variants } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../../../GlobalProvider';
import { getOrDefault } from '../../../../Helpers/Utils';
import { getDataFromCollectionWithQuery } from '../../../../Helpers/dataUtils';
import { AsymmetricInfiniteScrollGridViewProps, GridItem } from './types';

const MasonryContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridGap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    padding: theme.spacing(2),
    width: '100%',

    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gridGap: theme.spacing(1.5),
        padding: theme.spacing(1),
    },

    [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gridGap: theme.spacing(2.5),
    },

    [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gridGap: theme.spacing(3),
    }
}));

const StyledCard = styled(Card)<{ itemHeight?: number }>(({ theme, itemHeight }) => ({
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    height: itemHeight,
    display: 'flex',
    flexDirection: 'column',

    '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',

        '& .overlay': {
            opacity: 1,
        },

        '& .card-media': {
            transform: 'scale(1.05)',
        },

        '& .action-buttons': {
            opacity: 1,
            transform: 'translateY(0)',
        }
    }
}));

const StyledCardMedia = styled(CardMedia)({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 1,
    filter: 'brightness(0.7)',
    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
});

const Overlay = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.7) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    zIndex: 2,
});

const StyledCardContent = styled(CardContent)({
    position: 'relative',
    zIndex: 3,
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-end',
    height: '100%',
});

const SkeletonCard = styled(Card)<{ itemHeight?: number }>(({ theme, itemHeight }) => ({
    borderRadius: 16,
    overflow: 'hidden',
    height: itemHeight,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
}));

const AsymmetricInfiniteScrollGridView: React.FC<AsymmetricInfiniteScrollGridViewProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Component configuration
    const componentSpec = getOrDefault(spec, {});
    const sectionWidth = getOrDefault(componentSpec.sectionWidth, '100%');
    const backgroundColor = getOrDefault(componentSpec.backgroundColor, 'transparent');
    const collectionToFetchFrom = getOrDefault(componentSpec.collectionToFetchFrom, 'defaultCollection');
    const sectionTitle = getOrDefault(componentSpec.sectionTitle, '');
    const titleColor = getOrDefault(componentSpec.titleColor, '#333');
    const hoverAccentColor = getOrDefault(componentSpec.hoverAccentColor, theme.palette.primary.main);
    const getItemsHrefFunction = getOrDefault(componentSpec.getItemsHrefFunction, (_: GridItem) => '#');
    const wheres = getOrDefault(componentSpec.wheres, []);

    const scrollRef = useRef<HTMLDivElement>(null);
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { firestore } = cloudProvider;
    const [menuItems, setMenuItems] = useState<GridItem[]>([]);
    const [dataRetrieved, setDataRetrieved] = useState(false);

    // Generate random heights for asymmetric layout
    const getRandomHeight = useCallback((): number => {
        const heights = isMobile
            ? [220, 250, 280]
            : isTablet
                ? [250, 280, 310]
                : [320, 360, 400];
        return heights[Math.floor(Math.random() * heights.length)];
    }, [isMobile, isTablet]);

    // Initial data fetch
    useEffect(() => {
        if (firestore && collectionToFetchFrom) {
            getDataFromCollectionWithQuery(
                (data: any[]) => {
                    const itemsWithHeight: GridItem[] = data.map((item: any) => ({
                        ...item,
                        height: getRandomHeight()
                    }));
                    setMenuItems(itemsWithHeight);
                    setDataRetrieved(true);
                },
                setDataRetrieved,
                firestore.instance,
                collectionToFetchFrom,
                wheres,
                true, // useCache
                25, // Fetch up to 25 items initially
            );
        }
    }, [firestore, collectionToFetchFrom, getRandomHeight, wheres]);

    // Render skeleton cards
    const renderSkeletonCards = () => {
        return Array.from({ length: 12 }).map((_, idx) => (
            <SkeletonCard key={`skeleton-${idx}`} itemHeight={getRandomHeight()}>
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        backgroundColor: '#f5f5f5',
                    }}
                />
                <CardContent
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'flex-end',
                        height: '100%',
                    }}
                >
                    <Skeleton
                        variant="text"
                        width="80%"
                        height={32}
                        animation="wave"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.8)' }}
                    />
                </CardContent>
            </SkeletonCard>
        ));
    };

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.9
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring" as const,
                stiffness: 100,
                damping: 12
            }
        }
    };

    // Loading state
    if (!dataRetrieved && !menuItems.length) {
        return (
            <Box sx={{ background: backgroundColor, minHeight: '100vh', pt: 4 }}>
                {sectionTitle && (
                    <Box sx={{ textAlign: 'center', mb: 4, px: 3 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: titleColor,
                                fontWeight: 800,
                                fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                                mb: 2
                            }}
                        >
                            {sectionTitle}
                        </Typography>
                    </Box>
                )}
                <MasonryContainer>
                    {renderSkeletonCards()}
                </MasonryContainer>
            </Box>
        );
    }

    return (
        <Box
            id={spec.id}
            sx={{
                background: backgroundColor,
            }}>
            <Box
                ref={scrollRef}
                sx={{
                    minHeight: '100vh',
                    pt: 4,
                    pb: 4,
                    maxWidth: sectionWidth,
                    width: sectionWidth,
                    flexWrap: 'wrap',
                    justifySelf: 'center'
                }}
            >
                {/* Section Title */}
                {sectionTitle && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: "easeOut" as const }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 4, px: 3 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    color: titleColor,
                                    fontWeight: 800,
                                    fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                                    background: `linear-gradient(45deg, ${titleColor}, ${hoverAccentColor})`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    lineHeight: 1.2,
                                    mb: 2
                                }}
                            >
                                {sectionTitle}
                            </Typography>
                        </Box>
                    </motion.div>
                )}

                {/* Masonry Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    <MasonryContainer>
                        {menuItems.map((item: GridItem, idx: number) => (
                            <motion.div
                                key={item.id || idx}
                                variants={itemVariants}
                                layout
                            >
                                <StyledCard
                                    itemHeight={item.height}
                                    sx={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                    }}
                                >
                                    <CardActionArea
                                        component="a"
                                        href={getItemsHrefFunction(item)}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
                                    >
                                        {/* Image */}
                                        <StyledCardMedia
                                            className="card-media"
                                            image={item.imageSrc}
                                            title={item.name}
                                        />

                                        {/* Content overlaying the image */}
                                        <StyledCardContent>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                                                    fontSize: '1.6rem',
                                                    textAlign: 'center',
                                                    width: '100%',
                                                    lineHeight: 1.4,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                        </StyledCardContent>
                                    </CardActionArea>

                                    {/* Hover Overlay */}
                                    <Overlay className="overlay" />
                                </StyledCard>
                            </motion.div>
                        ))}
                    </MasonryContainer>
                </motion.div>
            </Box>
        </Box>
    );
};

export default AsymmetricInfiniteScrollGridView;
